import type { EpocusEncounter } from "$global-providers/rxdb-provider/collections/epocus-encounter";
import type { EpocusReview } from "$global-providers/rxdb-provider/collections/epocus-review";

export const DEFAULT_TURNAROUND_TARGET_MINUTES = 30;

export type TurnaroundStatus = "within_target" | "over_target";

const MINUTE_MS = 60 * 1000;

const parseTime = (value: string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

export const getTurnaroundStartedAt = (
  encounter: Pick<EpocusEncounter, "assignedAt" | "submittedForReviewAt">,
): number | null =>
  parseTime(encounter.assignedAt) ?? parseTime(encounter.submittedForReviewAt);

export const getTurnaroundMinutes = (
  review: Pick<EpocusReview, "signedAt">,
  encounter: Pick<EpocusEncounter, "assignedAt" | "submittedForReviewAt">,
): number | null => {
  const signedAt = parseTime(review.signedAt);
  const startedAt = getTurnaroundStartedAt(encounter);
  if (signedAt === null || startedAt === null) return null;
  const elapsed = signedAt - startedAt;
  return elapsed >= 0 ? elapsed / MINUTE_MS : null;
};

export const getTurnaroundStatus = (
  minutes: number,
  targetMinutes: number,
): TurnaroundStatus =>
  minutes <= targetMinutes ? "within_target" : "over_target";

export const resolveTargetMinutes = (
  turnaroundTargetMinutes: number | null | undefined,
): number => turnaroundTargetMinutes ?? DEFAULT_TURNAROUND_TARGET_MINUTES;

export const median = (values: number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2
    : (sorted[mid] ?? null);
};
